import React, { useCallback, useContext, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { getAPIEndpoint } from '../../../common/getAPIEndpoint';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import { validateJson } from '../../../components/common/chatbot/validation/validator';
import Preloader from '../../../components/common/Preloader';
import { AppInfoContext } from '../../../components/contexts/AppInfoContext';
import SystemPage from '../../../components/system-page/SystemPage';
import { Behavior } from '../../../types/behaviors';
import {
	ListForcePackageData,
	Optional,
	SystemSettingsBaseTypes,
	SystemSettingsObjectType,
} from '../../../types/common';
import { FiltersData, InputData, SystemData } from '../../../types/systems';
import { defaultSettings, systemsSettingsTemplate } from './defaultSettings';

const Systems: React.FC = () => {
	const [filters, setFilters] = useState<FiltersData>();
	const [inputData, setInputData] = useState<InputData>();
	const { currentPid } = useContext(AppInfoContext);

	const initialize = useCallback(
		async (pid: number, fp: number | string, side: number | string) => {
			const filters = await downloadFilters(pid, fp, side);
			const inpData = await downloadInputData();

			if (!filters || !inpData) {
				return false;
			}

			setFilters(filters);
			setInputData(inpData);
			return true;
		},
		[]
	);

	const downloadFilters = async (pid: number, fp: number | string, side: number | string) => {
		return await makeSafeRequest<FiltersData>('systems/', 'GET', {
			params: { pid: pid, fp: fp, side: side },
		});
	};

	const downloadSystemSettings = async (systemId: number) => {
		return await makeSafeRequest<SystemData>(`systems/${systemId}/`, 'GET');
	};

	const downloadInputData = async () => {
		return await makeSafeRequest<InputData>('systems/input-data/', 'GET');
	};

	const addSystem = async (settings: Optional<SystemData, 'id'>) => {
		if (!validateJson({ ...settings, id: -1 }, 'system')) {
			notifications.show({
				title: 'Invalid system settings',
				message: 'Please check the settings and try again.',
				color: 'red',
			});

			return null;
		}

		return await makeSafeRequest<SystemData>('systems/', 'POST', {
			payload: settings,
			onSuccessMessage: 'System added successfully!',
		});
	};

	const deleteSystem = async (settingsId: number) => {
		return await makeSafeRequest<null>(`systems/${settingsId}/`, 'DELETE', {
			onSuccessMessage: 'System deleted successfully!',
		});
	};

	const saveSystem = async (settings: Optional<SystemData, 'id'>) => {
		if (!validateJson({ ...settings, id: -1 }, 'system')) {
			notifications.show({
				title: 'Invalid system settings',
				message: `Please check the settings and try again.`,
				color: 'red',
			});

			return null;
		}

		return await makeSafeRequest<SystemData>(`systems/${settings.id}/`, 'PUT', {
			payload: settings,
			onSuccessMessage: 'System saved successfully!',
		});
	};

	const duplicateSystem = async (settingsId: number) => {
		return await makeSafeRequest<SystemData>(`systems/${settingsId}/duplicate/`, 'POST', {
			onSuccessMessage: 'System duplicated successfully!',
		});
	};

	const getSystemUsageInfo = async (settingsId: number, pid: number) => {
		return await makeSafeRequest<SystemData>(`systems/${settingsId}/usage-info/`, 'GET', {
			params: { pid: pid },
		});
	};

	useEffect(() => {
		if (inputData && systemsSettingsTemplate) {
			if (inputData.behaviors) {
				const toSelectable = (behaviors: Behavior[]) => {
					return behaviors.map((behavior: Behavior) => {
						return { value: behavior.name, label: behavior.name };
					});
				};
				console.log(inputData.behaviors);

				const movingBehaviors = {
					type: 'select' as SystemSettingsBaseTypes,
					data: toSelectable(
						inputData.behaviors.filter(
							(behavior) => behavior.type === 'MOVING_BEHAVIOR'
						)
					),
				};

				const planningActions = {
					type: 'select' as SystemSettingsBaseTypes,
					data: toSelectable(
						inputData.behaviors.filter(
							(behavior) =>
								behavior.type === 'PLANNING_ACTION' && behavior.template === null
						)
					),
				};

				systemsSettingsTemplate.sections['Behaviors'].blue_subordinate_behavior =
					movingBehaviors;
				systemsSettingsTemplate.sections['Behaviors'].red_subordinate_behavior =
					movingBehaviors;
				systemsSettingsTemplate.sections['Behaviors'].blue_commander_behavior =
					planningActions;
				systemsSettingsTemplate.sections['Behaviors'].red_commander_behavior =
					planningActions;
			}

			if (inputData.roles) {
				const data = inputData.roles.map((role: string) => {
					return { value: role, label: role };
				});
				(systemsSettingsTemplate.sections['Marks'].roles as SystemSettingsObjectType).data =
					data;
			}

			if (inputData.icons) {
				const data = inputData.icons.map((icon: { icon: string; url: string }) => {
					return {
						value: icon.icon,
						label: icon.icon.replace(/\.(png|jpe?g|gif|webp|svg)$/i, ''),
						image: `${getAPIEndpoint()}site_media/systems_icons/${icon.icon}`,
					};
				});
				(systemsSettingsTemplate.sections['Sensor'].icon as SystemSettingsObjectType).data =
					data;
			}
		}
	}, [inputData]);

	useEffect(() => {
		initialize(currentPid, '', '');
	}, [initialize, currentPid]);

	return filters && inputData ? (
		<SystemPage
			name="system"
			forcePackageData={filters.fps as ListForcePackageData[]}
			systemsData={filters.systems as SystemData[]}
			inputData={inputData}
			initializeFilters={initialize}
			downloadSystemSettings={downloadSystemSettings}
			systemSettingsTemplate={systemsSettingsTemplate}
			defaultSettings={defaultSettings}
			addSystem={addSystem}
			saveSystem={saveSystem}
			deleteSystem={deleteSystem}
			duplicateSystem={duplicateSystem}
			getSystemUsageInfo={getSystemUsageInfo}
		></SystemPage>
	) : (
		<Preloader></Preloader>
	);
};

export default Systems;
